import { useEffect, useMemo, useState, RefObject } from 'react'
import TouchableOpacity from '../../TouchableOpacity'
import Text from '../../Text'
import ListRow from '../../ListRow'
import ListRowAction from '../../ListRowAction'
import DownloadIcon from '../../../assets/icons/download'
import { exportPlaylist } from '../../../api/endpoints'
import styles from './style'

export interface GroupFilterViewHandle {
  show: () => void
}

interface GroupFilterViewModalProps {
  groups: string[]
  selectedGroups: string[]
  onSelectedGroupsChange: (groups: string[]) => void
  ref?: RefObject<GroupFilterViewHandle | null>
}

interface GroupCategory {
  key: string
  members: string[]
}

// Playlist groups follow a "<code>| Name" or "<code> - Name" convention
// (e.g. "DE| BUNDESLIGA", "DE - FUSSBALL"), so bucketing by that leading
// code groups everything for one language/provider together (e.g. all
// "DE" groups) and lets an admin toggle them as one. Groups that don't
// follow the convention just become their own single-item bucket.
function categoryKey(group: string): string {
  const match = group.match(/^([A-Za-z0-9]{2,6})\s*[|-]/)
  return match ? match[1].toUpperCase() : group
}

function categorize(groups: string[]): GroupCategory[] {
  const buckets = new Map<string, string[]>()
  for (const group of groups) {
    const key = categoryKey(group)
    const bucket = buckets.get(key)
    if (bucket) bucket.push(group)
    else buckets.set(key, [group])
  }

  return [...buckets.entries()]
    .map(([key, members]) => ({ key, members: members.sort() }))
    .sort((a, b) => a.key.localeCompare(b.key))
}

function GroupFilterViewModal({
  groups,
  selectedGroups,
  onSelectedGroupsChange,
  ref,
}: GroupFilterViewModalProps) {
  const [show, setShow] = useState(false)
  const [expandedCategories, setExpandedCategories] = useState<Set<string>>(new Set())
  const [useTunnel, setUseTunnel] = useState(false)

  const categories = useMemo(() => categorize(groups), [groups])

  useEffect(() => {
    if (!ref) return

    ref.current = {
      show: () => setShow(true),
    }
  }, [ref])

  function toggleGroup(group: string) {
    onSelectedGroupsChange(
      selectedGroups.includes(group)
        ? selectedGroups.filter(item => item !== group)
        : [...selectedGroups, group]
    )
  }

  function categoryState(members: string[]): 'all' | 'some' | 'none' {
    const selectedCount = members.filter(group => selectedGroups.includes(group)).length
    if (selectedCount === 0) return 'none'
    return selectedCount === members.length ? 'all' : 'some'
  }

  function toggleCategory(members: string[]) {
    const isFullySelected = categoryState(members) === 'all'
    onSelectedGroupsChange(
      isFullySelected
        ? selectedGroups.filter(group => !members.includes(group))
        : [...new Set([...selectedGroups, ...members])]
    )
  }

  function toggleAll() {
    onSelectedGroupsChange(categoryState(groups) === 'all' ? [] : [...groups])
  }

  function toggleExpanded(key: string) {
    setExpandedCategories(previous => {
      const next = new Set(previous)
      if (next.has(key)) next.delete(key)
      else next.add(key)
      return next
    })
  }

  async function downloadPlaylist() {
    const result = await exportPlaylist(selectedGroups, useTunnel)
    if (!result.ok) return

    const url = URL.createObjectURL(new Blob([result.data], { type: 'audio/x-mpegurl' }))
    const link = document.createElement('a')
    link.href = url
    link.download = 'playlist.m3u'
    link.click()
    URL.revokeObjectURL(url)
  }

  return show ? (
    <div style={styles.container}>
      <div
        style={styles.backdrop}
        onClick={() => setShow(false)}
      />

      <div style={styles.panel}>
        <div style={styles.header}>
          <Text style={styles.headerTitle}>Gruppen filtern</Text>
          <div style={styles.headerActions}>
            <ListRowAction onClick={downloadPlaylist}>
              <DownloadIcon size={18} />
            </ListRowAction>
            <TouchableOpacity onClick={() => setShow(false)}>
              <Text style={styles.closeIcon}>✕</Text>
            </TouchableOpacity>
          </div>
        </div>

        <div style={styles.list}>
          {categories.length === 0 ? (
            <Text style={styles.emptyText}>Keine Gruppen vorhanden</Text>
          ) : (
            <ListRow
              leading={
                <Text style={styles.checkbox}>
                  {categoryState(groups) === 'all'
                    ? '☑'
                    : categoryState(groups) === 'some'
                      ? '◪'
                      : '☐'}
                </Text>
              }
              label={`Alle Gruppen (${selectedGroups.length}/${groups.length})`}
              onClick={toggleAll}
            />
          )}
          {categories.map(({ key, members }) => {
            if (members.length === 1) {
              const group = members[0]
              return (
                <ListRow
                  key={group}
                  leading={
                    <Text style={styles.checkbox}>
                      {selectedGroups.includes(group) ? '☑' : '☐'}
                    </Text>
                  }
                  label={group}
                  onClick={() => toggleGroup(group)}
                />
              )
            }

            const state = categoryState(members)
            const isExpanded = expandedCategories.has(key)

            return (
              <div key={key}>
                <ListRow
                  leading={
                    <TouchableOpacity
                      style={styles.categoryCheckbox}
                      onClick={e => {
                        e.stopPropagation()
                        toggleCategory(members)
                      }}
                    >
                      <Text style={styles.checkbox}>
                        {state === 'all' ? '☑' : state === 'some' ? '◪' : '☐'}
                      </Text>
                    </TouchableOpacity>
                  }
                  label={`${key} (${members.length})`}
                  onClick={() => toggleExpanded(key)}
                  actions={
                    <ListRowAction onClick={() => toggleExpanded(key)}>
                      <Text style={styles.chevron}>{isExpanded ? '▾' : '▸'}</Text>
                    </ListRowAction>
                  }
                />
                {isExpanded ? (
                  <div style={styles.categoryMembers}>
                    {members.map(group => (
                      <ListRow
                        key={group}
                        leading={
                          <Text style={styles.checkbox}>
                            {selectedGroups.includes(group) ? '☑' : '☐'}
                          </Text>
                        }
                        label={group}
                        onClick={() => toggleGroup(group)}
                      />
                    ))}
                  </div>
                ) : null}
              </div>
            )
          })}
        </div>

        <div style={styles.footer}>
          <ListRow
            leading={<Text style={styles.checkbox}>{useTunnel ? '☑' : '☐'}</Text>}
            label='Tunnel verwenden'
            onClick={() => setUseTunnel(previous => !previous)}
          />
        </div>
      </div>
    </div>
  ) : null
}

export default GroupFilterViewModal
